import { computeOrderTotals } from "@/features/shared";
import { AddProductVariantSection } from "@/features/ui";
import { yupResolver } from "@hookform/resolvers/yup";
import { Divider, Paper } from "@mui/material";
import Grid from "@mui/material/Grid";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { ulid } from "ulid";
import { GeneralInformationSection } from "./general-information-section";
import { GeneralProductSection } from "./general-product-section";
import { OtherSection } from "./other-section";
import { PriceSummarySection } from "./price-summary-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./sales-order-manual-form-constants";

export const SalesOrderManualForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
}) => {
  const methods = useForm({
    defaultValues: defaultValues
      ? { ...DEFAULT_VALUES, ...defaultValues }
      : DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const { control, watch, setValue } = methods;

  const salesOrderProductVariants = watch("salesOrderProductVariants");
  const seller = watch("seller");

  const { append } = useFieldArray({
    control,
    name: "salesOrderProductVariants",
  });

  useEffect(() => {
    const salesOrderProductVariantTotalPrice = salesOrderProductVariants.reduce(
      (acc, { quantity, costPerUnit, discount }) =>
        acc + quantity * costPerUnit - (discount || 0),
      0
    );
    setValue("subtotal", salesOrderProductVariantTotalPrice);
  }, [salesOrderProductVariants, setValue]);

  const onFormSubmit = async (formData) => {
    const { taxAmount, grandTotal } = computeOrderTotals(formData);

    const payload = {
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      sellerId: formData.seller.id,
      buyerableId: formData.buyer?.id,
      warehouseId: formData.warehouse.id,
      discountType: formData.discountType,
      discountAmount: formData.discountInput || 0,
      grandTotal,
      paymentTermId: formData.paymentTerm.id,
      taxable: formData.taxable,
      totalIncludesTax: formData.taxable && formData.totalIncludesTax,
      taxPercentage: formData.taxPercentage,
      taxAmount,
      lensProducts: [],
      generalProductVariants: formData.salesOrderProductVariants.map(
        ({ sku, quantity, note, discount }) => ({
          sku,
          quantity,
          note,
          discount: discount || 0,
        })
      ),
    };

    await onSubmit(payload);
  };

  return (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={methods.handleSubmit(onFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        spacing={3}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <AddProductVariantSection
              outletId={seller?.id}
              onAdded={(productVariant, product, quantity) => {
                append({
                  id: ulid(),
                  productVariantId: productVariant.id,
                  name: productVariant.name,
                  sku: productVariant.sku,
                  unitOfMeasurement: product.unitOfMeasurement.name,
                  costPerUnit: productVariant.distributorPrice,
                  discount: 0,
                  quantity,
                });
              }}
            />
            <Divider sx={{ my: 2.5 }} />
            <GeneralProductSection />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <OtherSection />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <PriceSummarySection />
        </Grid>
        <Grid size={{ xs: 12 }}>
          {renderActionButton({ isSubmitting: methods.formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

SalesOrderManualForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
};
